/**
 * Client-side feature flags. Source of truth in priority order:
 *
 *   1. localStorage override — `feature.<flag>` set to '1' | 'true' enables,
 *      '0' | 'false' disables. Lets devs flip flags without rebuild.
 *   2. Vite env — `import.meta.env.VITE_FEATURE_<FLAG>` ('1' enables).
 *   3. Built-in default — falsy unless listed in DEFAULTS below.
 *
 * Why this exists: features that ship behind a flag because their wiring is
 * incomplete (e.g. RoleplayMode whose RP writes are silently dropped — see
 * docs/backlog/charsheet-hud-verification-2026-05-17.md → CRITICAL FINDING)
 * still need to be discoverable for developers, but must not reach end users.
 */

const DEFAULTS: Record<string, boolean> = {
  // RoleplayMode: enabled by default so the Отыгрыш tab renders.
  roleplayMode: true,
};

function readLocalStorage(flag: string): boolean | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(`feature.${flag}`);
    if (raw === null) return null;
    if (raw === '1' || raw.toLowerCase() === 'true') return true;
    if (raw === '0' || raw.toLowerCase() === 'false') return false;
    return null;
  } catch {
    return null;
  }
}

function readEnv(flag: string): boolean | null {
  // Vite injects import.meta.env at build time; in non-vite contexts (tests
  // that don't load the bundler) it may be undefined.
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  if (!env) return null;
  const key = `VITE_FEATURE_${flag.replace(/([A-Z])/g, '_$1').toUpperCase()}`;
  const raw = env[key];
  if (raw === undefined) return null;
  if (raw === '1' || raw.toLowerCase() === 'true') return true;
  if (raw === '0' || raw.toLowerCase() === 'false') return false;
  return null;
}

export function isFeatureEnabled(flag: string): boolean {
  const fromLocal = readLocalStorage(flag);
  if (fromLocal !== null) return fromLocal;
  const fromEnv = readEnv(flag);
  if (fromEnv !== null) return fromEnv;
  return DEFAULTS[flag] ?? false;
}

export function isRoleplayModeEnabled(): boolean {
  return isFeatureEnabled('roleplayMode');
}
